import * as React from "react";
import { useToggle } from "react-use";
import { toast } from "react-toastify";
import { TrafficLightRating } from "@/modules/snapshots/businessReports.types";
import { useUpdateBusinessReportOutcomeResultMutation } from "../../../businessReports.api";
import { getResultUpdateErrorMessage } from "@/modules/snapshots/resultUpdateErrors";

interface UseContentEditorProps {
  initialContent: string;
  initialRating: TrafficLightRating | null;
  isTrafficLight: boolean;
  businessReportOutcomeResultId: string;
  businessReportOutcomeId: string;
}

export const useContentEditor = ({
  initialContent,
  initialRating,
  isTrafficLight,
  businessReportOutcomeResultId,
  businessReportOutcomeId,
}: UseContentEditorProps) => {
  const [isEdit, toggleIsEdit] = useToggle(false);
  const [editedContent, setEditedContent] = React.useState(initialContent);
  const [editedRating, setEditedRating] = React.useState<TrafficLightRating | null>(initialRating);
  const [updateBusinessReportOutcomeResult, { isLoading }] = useUpdateBusinessReportOutcomeResultMutation();

  React.useEffect(() => {
    setEditedContent(initialContent);
  }, [initialContent]);

  React.useEffect(() => {
    setEditedRating(initialRating);
  }, [initialRating]);

  const handleSave = async () => {
    try {
      const result = editedContent.replace(/<br>/g, "\n");
      const data: { result: string; rating?: TrafficLightRating | null } = { result };
      if (isTrafficLight) {
        data.rating = editedRating;
      }
      await updateBusinessReportOutcomeResult({
        resultId: businessReportOutcomeResultId,
        id: businessReportOutcomeId,
        data,
      }).unwrap();
      toast.success("Changes saved successfully");
      toggleIsEdit(false);
    } catch (error) {
      toast.error(getResultUpdateErrorMessage(error, "Error while saving"));
      console.error(error);
    }
  };

  const handleCancel = () => {
    setEditedContent(initialContent);
    setEditedRating(initialRating);
    toggleIsEdit(false);
  };

  return {
    isEdit,
    editedContent,
    editedRating,
    isLoading,
    toggleIsEdit,
    setEditedContent,
    setEditedRating,
    handleSave,
    handleCancel,
  };
};
